import React from 'react';
import PropTypes from 'prop-types';
import { Link, Routes, Route } from 'react-router-dom';

// Third-parties.
import Helmet from 'react-helmet';

// Types.
import InstanceTarget from '../../types/instance/InstanceTarget';
import HostTarget from '../../types/host/HostTarget';
import EventType from '../../types/instance/EventType';
import Agent from '../../types/instance/Agent';
import InstanaIntegration from '../../types/integration/InstanaIntegration';
import Licence from '../../types/Licence';

// Admin Views.
import Instances from './instances/Instances';
import Hosts from './hosts/Hosts';
import AlertRules from './alert-rules';
// import AlertActions from './alert-actions';
import EventTypes from './eventTypes/EventTypes';
import Agents from './agents/Agents';
import Instana from './integrations/instana/Instana';
import EmailIntegration from './integrations/email';
import Integrations from './integrations/';
import Licences from './licences/Licences';
import Snapshots from './snapshots/Snapshots';
import Breadcrumb from "../../container/breadcrumb";
import PeriodBreadcrumb from "../../component/PeriodBreadcrumb";
import HeaderActions from "../../container/header_actions";
import ExtendedStatistics from "./sql-statistics/";
import DatabaseStatistics from "./db-statistics/";
import ConditionalRender from "../../helpers/ConditionalRender";
import NewRelic from "./integrations/new-relic";
import PagerDuty from "./integrations/pager-duty";
import ServiceNow from "./integrations/service-now";
import Slack from "./integrations/slack";
import Webhooks from "./integrations/webhooks";
import Logins from "./logins/Logins";
import Tags from "./tags/Tags"; // Import the new Tags component
import Parameters from "./parameters/Parameters"; // Import the new Parameters component
import SelfMonitoring from "./self-monitoring";
import NotFound from "../NotFound";
import CoPilot from "./integrations/co-pilot";

function Admin(props: {
    instances: InstanceTarget[],
    hosts: HostTarget[],
    eventTypes: EventType[],
    agents: Agent[],
    instanaIntegrations: InstanaIntegration[],
    licences: Licence[],
    toggleMenu: Function,
    loginRequired: boolean,
    isAdmin: boolean
}) {

    return (
        <Routes>
            <Route path="/" element={
                <React.Fragment>
                    <div id="content">
                        <Helmet>
                            <title>Settings - DBmarlin</title>
                            <meta name="description" content="Settings and administration options within DBmarlin" />
                        </Helmet>

                        <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                            <Breadcrumb heading="Settings">
                                <Link to="/admin">Settings</Link>
                                <PeriodBreadcrumb />
                            </Breadcrumb>
                            <HeaderActions toggleMenu={props.toggleMenu} refresh={() => {
                            }} />
                        </div>

                        <div className="loader">
                            <ConditionalRender if={false}>
                                <div className="bar"></div>
                            </ConditionalRender>
                        </div>

                        <div className="row row-cols-1">
                            <div className="col h-100">
                                <div className="card">
                                    <div className="card-header">
                                        <i className="fal fa-cog fa-fw" aria-hidden="true" />
                                        Settings
                                        <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                            data-bs-target="#collapseSettings" aria-expanded="false"
                                            aria-controls="collapseSettings" />
                                    </div>
                                    <div id="collapseSettings" className="card-body collapse show">
                                        <p>Please select an option from below:</p>
                                        <ul>
                                            <li><Link to="/admin/instances">Manage Database Instances</Link></li>
                                            <li><Link to="/admin/hosts">Manage Hosts</Link></li>
                                            <li><Link to="/admin/database-extended-statistics">Manage SQL
                                                Statistics</Link>
                                            </li>
                                            <li><Link to="/admin/database-statistics">Manage
                                                Statistics</Link>
                                            </li>
                                            <li><Link to="/admin/alert-rules">Manage Alert Rules</Link></li>
                                            <li><Link to="/admin/filters">Manage Snapshots</Link></li>
                                            <li><Link to="/admin/integrations/">Manage Integrations</Link></li>
                                            <li><Link to="/admin/event-types">Manage Event Types</Link></li>
                                            <li><Link to="/admin/agents">Manage Agents</Link></li>
                                            <li><Link to="/admin/licences">Manage Licences</Link></li>
                                            <ConditionalRender if={props.loginRequired}>
                                                <li><Link to="/admin/logins">Manage Logins</Link></li>
                                            </ConditionalRender>
                                            {/* Add link to Manage Tags, assuming isAdmin is the correct condition */}
                                            <ConditionalRender if={props.isAdmin}>
                                                <li><Link to="/admin/tags">Manage Tags</Link></li>
                                            </ConditionalRender>
                                            <ConditionalRender if={props.isAdmin}>
                                                <li><Link to="/admin/parameters">Manage Parameters</Link></li>
                                            </ConditionalRender>
                                            <ConditionalRender if={props.isAdmin}>
                                                <li><Link to="/admin/self-monitoring">Self Monitoring</Link></li>
                                            </ConditionalRender>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            } />
            <Route path="/instances/*"
                element={<Instances isAdmin={props.isAdmin} instances={props.instances}
                    toggleMenu={props.toggleMenu} />} />
            <Route path="/hosts/*"
                element={<Hosts isAdmin={props.isAdmin} hosts={props.hosts}
                    toggleMenu={props.toggleMenu} />} />
            {/*<Route path="/alert-actions/*"*/}
            {/*       element={<AlertActions key={props.period.timestamp} period={props.period}*/}
            {/*                              toggleMenu={props.toggleMenu}*/}
            {/*                             />}/>*/}
            <Route path="/database-extended-statistics/*"
                element={<ExtendedStatistics toggleMenu={props.toggleMenu} />} />
            <Route path="/database-statistics/*"
                element={<DatabaseStatistics toggleMenu={props.toggleMenu} />} />
            <Route path="/alert-rules/*"
                element={<AlertRules isAdmin={props.isAdmin} toggleMenu={props.toggleMenu}
                    instances={props.instances} hosts={props.hosts}
                    />} />
            <Route path="/filters/*"
                element={<Snapshots toggleMenu={props.toggleMenu}
                    />} />
            <Route path="/event-types/*"
                element={<EventTypes eventTypes={props.eventTypes}
                    toggleMenu={props.toggleMenu} />} />
            <Route path="/agents/*"
                element={<Agents agents={props.agents} toggleMenu={props.toggleMenu} />} />

            <Route path="/integrations/instana/*"
                element={<Instana
                    instanaIntegrations={props.instanaIntegrations} toggleMenu={props.toggleMenu}
                    isAdmin={props.isAdmin} />} />

            <Route path="/integrations/email/*"
                element={<EmailIntegration toggleMenu={props.toggleMenu}
                    isAdmin={props.isAdmin} />} />

            <Route path="/integrations/new-relic/*"
                element={<NewRelic toggleMenu={props.toggleMenu}
                    isAdmin={props.isAdmin} />} />

            <Route path="/integrations/pager-duty/*"
                element={<PagerDuty toggleMenu={props.toggleMenu}
                    isAdmin={props.isAdmin} />} />

            <Route path="/integrations/co-pilot/*"
                element={<CoPilot toggleMenu={props.toggleMenu}
                    isAdmin={props.isAdmin} />} />

            <Route path="/integrations/service-now/*"
                element={<ServiceNow toggleMenu={props.toggleMenu}
                    isAdmin={props.isAdmin} />} />

            <Route path="/integrations/slack/*"
                element={<Slack toggleMenu={props.toggleMenu}
                    isAdmin={props.isAdmin} />} />

            <Route path="/integrations/webhooks/*"
                element={<Webhooks toggleMenu={props.toggleMenu}
                    isAdmin={props.isAdmin} />} />

            <Route path="/integrations/*"
                element={<Integrations isAdmin={props.isAdmin} toggleMenu={props.toggleMenu}
                    />} />
            <Route path="/licences/*"
                element={<Licences licences={props.licences}
                    toggleMenu={props.toggleMenu} />} />
            <Route path="/logins/*"
                element={<Logins isAdmin={props.isAdmin} loginRequired={props.loginRequired}
                    toggleMenu={props.toggleMenu} />} />
            {/* Add route for the Tags screen */}
            <Route path="/tags/*"
                element={<Tags isAdmin={props.isAdmin} loginRequired={props.loginRequired}
                    toggleMenu={props.toggleMenu} instances={props.instances} />} />
            {/* Add route for the Parameters screen */}
            <Route path="/parameters/*"
                element={<Parameters isAdmin={props.isAdmin} loginRequired={props.loginRequired}
                    toggleMenu={props.toggleMenu} />} />
            <Route path="/self-monitoring/*"
                element={<SelfMonitoring isAdmin={props.isAdmin} toggleMenu={props.toggleMenu} />} />

            <Route path="*" element={<NotFound />} />
        </Routes>
    );
}

Admin.propTypes = {
    toggleMenu: PropTypes.func.isRequired,
};

export default Admin;
